import { useEffect, useState } from "react";

import { flushSync } from "react-dom";

/** The selected text inside `container`, clipped to it; null if none. */
const getSelectedTextWithin = (container: HTMLElement): string | null => {
  const selection = container.ownerDocument.defaultView?.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!range.intersectsNode(container)) return null;
  // A drag that runs past the container keeps only the container's part.
  const clipped = range.cloneRange();
  if (!container.contains(range.startContainer)) clipped.setStart(container, 0);
  if (!container.contains(range.endContainer)) {
    clipped.setEnd(container, container.childNodes.length);
  }
  return clipped.toString().trim() || null;
};

/**
 * Tracks whether text inside `element` is selected (MDRS-486).
 *
 * Follows the selection itself (`selectionchange` on the element's own
 * document), not mouse releases over the element: a drag that ends outside
 * it, a keyboard selection, or a click elsewhere that clears it all register
 * straight away — including when the element is rendered into another window.
 * Pass the element from a callback ref kept in state, so the hook
 * re-subscribes when it remounts into another document.
 *
 * Only whether there is a selection is state; read the text itself with
 * `getSelectedText()` when it's needed, so a drag doesn't re-render the owner
 * on every step.
 */
export const useSelectedTextWithin = (element: HTMLElement | null) => {
  const [hasSelection, setHasSelection] = useState(false);

  useEffect(() => {
    if (!element) return;
    const doc = element.ownerDocument;
    let reported = false;

    const handleSelectionChange = () => {
      if (!element.isConnected) return;
      const selected = getSelectedTextWithin(element) !== null;
      if (selected === reported) return;
      reported = selected;
      // From another window, React can't tell this is a user event (it reads
      // the main window's `window.event`), so it would queue an
      // ordinary-priority render instead of an immediate one. Flush now so UI
      // that depends on the selection keeps up.
      flushSync(() => setHasSelection(selected));
    };

    doc.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      doc.removeEventListener("selectionchange", handleSelectionChange);
      // A selection in the old element is gone with it.
      setHasSelection(false);
    };
  }, [element]);

  return {
    hasSelection,
    getSelectedText: () => (element ? getSelectedTextWithin(element) : null),
    clearSelection: () =>
      element?.ownerDocument.defaultView?.getSelection()?.removeAllRanges(),
  };
};
